import { Component, OnInit, OnDestroy } from '@angular/core';
import { Platform } from '@ionic/angular';
import { App } from '@capacitor/app';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subscription, take } from 'rxjs';

import { Store } from '@ngrx/store';
import { State } from './store/states/state.model';
import { userConnected } from './store/selectors/auth.selector';

import { OnesignalService } from './services/onesignal.service';
import { OrderNotificationService } from './services/app/order-notification.service';
import { NotificationHandlerService } from './services/notification-handler.service';
import { VersionCheckService } from './services/version-check/version-check.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private authSub: Subscription;
  private routerSub: Subscription;
  private notifSub: Subscription;
  private notifOpenedSub: Subscription;

  public badgeCount = 0;
  isDarkMode = false;

  constructor(
    private platform: Platform,
    private router: Router,
    private store: Store<State>,
    private onesignal: OnesignalService,
    private orderService: OrderNotificationService,
    private notifHandler: NotificationHandlerService,
    private versionService: VersionCheckService
  ) {
    this.platform.backButton.subscribeWithPriority(-1, () => App.exitApp());
    this.initializeApp();
  }

  ngOnInit() {
  this.versionService.startPolling();

  // 🔑 Surveiller l'utilisateur connecté
  this.authSub = this.store.select(userConnected).subscribe(user => {
    if (user?.id) {
      // 🔁 Polling commandes
      this.orderService.startPolling(2, user.id);

      // 🔔 Attendre que OneSignal soit prêt
      this.onesignal.isReady$
        .pipe(
          filter(Boolean),
          take(1)
        )
        .subscribe(async () => {

          this.onesignal.setExternalUserId(String(user.id));

          this.onesignal.setUserTags({
            user_id: String(user.id),
            restaurant_id: String(user.restaurantId),
            role: user.role
          });

          const playerId = await this.onesignal.getPlayerIdAsync();

          if (playerId) {
            this.onesignal.registerPlayerIdOnServer(playerId, user.id);
          }
        });


    } else {
      // 🚪 Déconnexion
      this.orderService.clearAllOrders();
      this.onesignal.unregister();
    }
  });

  // 🔄 Navigation
  this.routerSub = this.router.events
    .pipe(filter(e => e instanceof NavigationEnd))
    .subscribe((event: NavigationEnd) => {
      if (event.url === '/login') {
        this.orderService.clearAllOrders();
      }
      this.versionService.checkVersion();
    });

  // 📱 Notifications reçues
  this.notifSub = this.onesignal.notification$.subscribe(notification => {
    if (notification) {
      this.handleNotification(notification);
    }
  });

  // 👆 Notifications ouvertes
  this.notifOpenedSub = this.onesignal.notificationOpened$.subscribe(event => {
    if (event) {
      this.handleNotificationOpened(event);
    }
  });

  // 🔔 Badge
  this.orderService.ordersToPickup$.subscribe(orders => {
    this.badgeCount = orders.length;
  });
}


  ngOnDestroy() {
    this.versionService.stopPolling();
    this.authSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.notifSub?.unsubscribe();
    this.notifOpenedSub?.unsubscribe();
    this.orderService.clearAllOrders();
  }

  initializeApp() {
    this.platform.ready().then(() => {
      console.log('🚀 Platform ready, initialisation OneSignal...');
      this.onesignal.initOneSignal();
      this.initDarkMode();
    });
  }

  private initDarkMode() {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) {
      this.isDarkMode = saved === 'true';
    } else {
      this.isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    document.body.classList.toggle('dark', this.isDarkMode);
  }

  toggleDarkMode() {
    this.isDarkMode = !this.isDarkMode;
    document.body.classList.toggle('dark', this.isDarkMode);
    localStorage.setItem('darkMode', String(this.isDarkMode));
  }

  // 📱 Notification reçue (app en foreground)
  private handleNotification(notification: any) {
    console.log('🔔 Traitement notification foreground:', notification);
    
    // Afficher toast et jouer son
    this.notifHandler.showNotificationToast(notification);
    
    // Sync avec le serveur si nécessaire
    this.orderService.checkOrdersFromServerOnce();
    
    // Extraire les données additionnelles
    const additionalData = notification.additionalData || {};
    console.log('📦 Additional data:', additionalData);
  }

  // 👆 Notification cliquée (app fermée ou background)
  private handleNotificationOpened(event: any) {
    console.log('🔔 Traitement notification opened:', event);
    
    this.notifHandler.handleNotificationOpened(event);

    // Récupérer la notification depuis l'événement
    const notification = event.notification;
    const additionalData = notification?.additionalData || {};
    
    console.log('📦 Notification data:', notification);
    console.log('📦 Additional data:', additionalData);

    // Deep-link : ouvrir page table/commande
    if (additionalData?.orderId && additionalData?.table) {
      console.log(`🔗 Navigation vers /table/${additionalData.table}/order/${additionalData.orderId}`);
      this.router.navigate(['/table', additionalData.table, 'order', additionalData.orderId]);
    } else if (additionalData?.route) {
      // Alternative : route directe dans les données
      console.log(`🔗 Navigation vers ${additionalData.route}`);
      this.router.navigateByUrl(additionalData.route);
    }
    
    // Sync avec le serveur
    this.orderService.checkOrdersFromServerOnce();
  }
}